import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Divider } from 'primereact/divider';
import moment from 'moment';
import {
    getClientsList,
    getBillFilterHeader,
    formatDateForApi,
    exportCounterProducts,
    exportCounterCategories,
    exportCounterBillReport,
    exportCounterItemReport,
    exportCounterCategoryReport,
    exportOrdersProducts,
    exportOrdersCategories,
    exportOrdersCustomersProducts,
    exportOrdersItemReport,
    exportOrdersCategoryReport,
    exportOrdersCustomerReport
} from '../utilities/api';
import { exportStyledExcel } from '../utilities/excelExport';
import './ExcelExport.css';

const formatDate = (d) => (moment(d).isValid() ? moment(d).format('DD-MM-YYYY') : d || '-');
const num = (v) => parseFloat(v || 0);

const counterProductsColumns = [
    { header: 'Product ID', getValue: (r) => r.pr_id || '-' },
    { header: 'Product Name', getValue: (r) => r.product_name || '-' },
    { header: 'Product Code', getValue: (r) => r.product_code || '-' },
    { header: 'HSN Code', getValue: (r) => r.hsn_code || '-' },
    { header: 'UOM', getValue: (r) => r.uom || '-' },
    { header: 'Basic Rate', getValue: (r) => num(r.basic_rate), numeric: true },
    { header: 'Tax %', getValue: (r) => num(r.basic_tax_percent), numeric: true, skipSum: true },
    { header: 'Price', getValue: (r) => num(r.price), numeric: true },
    { header: 'Discount %', getValue: (r) => num(r.discount_perc), numeric: true, skipSum: true },
    { header: 'Type', getValue: (r) => r.product_type || '-' },
    { header: 'Status', getValue: (r) => r.product_status || '-' },
    { header: 'Category', getValue: (r) => r.category_name || '-' }
];

const categoryColumns = [
    { header: 'Category ID', getValue: (r) => r.prcat_cat_id || '-' },
    { header: 'Category Name', getValue: (r) => r.prcat_cat_name || '-' },
    { header: 'Reference Name', getValue: (r) => r.prcat_ref_name || '-' },
    { header: 'Discount %', getValue: (r) => num(r.prcat_discount), numeric: true, skipSum: true },
    { header: 'Status', getValue: (r) => r.prcat_status || '-' }
];

const billReportColumns = [
    { header: 'Order No', getValue: (r) => r.order_no || '-' },
    { header: 'Order Date', getValue: (r) => r.order_date || '-' },
    { header: 'Bill Time', getValue: (r) => r.bill_time || '-' },
    { header: 'In No', getValue: (r) => r.in_no || '-' },
    { header: 'Invoice No', getValue: (r) => r.invoice_no || '-' },
    { header: 'Invoice Date', getValue: (r) => r.invoice_date || '-' },
    { header: 'Invoice Time', getValue: (r) => r.invoice_time || '-' },
    { header: 'Edit Date', getValue: (r) => r.edit_date || '-' },
    { header: 'Edit Time', getValue: (r) => r.edit_time || '-' },
    { header: 'Customer Name', getValue: (r) => r.cust_name || '-' },
    { header: 'Customer Phone', getValue: (r) => r.cust_phone || '-' },
    { header: 'Order Taken By', getValue: (r) => r.ord_taken_by || '-' },
    { header: 'Order Edited By', getValue: (r) => r.ord_edited_by || '-' },
    { header: 'Basic Price', getValue: (r) => num(r.total_basic_price), numeric: true },
    { header: 'Discount', getValue: (r) => num(r.disc_total_amt), numeric: true },
    { header: 'Basic After Discount', getValue: (r) => num(r.basic_aft_disc), numeric: true },
    { header: 'Tax After Discount', getValue: (r) => num(r.tax_aft_disc), numeric: true },
    { header: 'Discount Given By', getValue: (r) => r.disc_given_by || '-' },
    { header: 'Discount Given To', getValue: (r) => r.disc_given_to || '-' },
    { header: 'Total Amount', getValue: (r) => num(r.total_amount), numeric: true },
    { header: 'Advance Amount', getValue: (r) => num(r.advance_amount), numeric: true },
    { header: 'Balance Amount', getValue: (r) => num(r.balance_amount), numeric: true },
    { header: 'Paid Amount', getValue: (r) => num(r.paid_amt), numeric: true },
    { header: 'Total Paid Amount', getValue: (r) => num(r.total_paid_amount), numeric: true },
    { header: 'MOP', getValue: (r) => r.mop || '-' },
    { header: 'Final MOP', getValue: (r) => r.final_mop || '-' },
    { header: 'Delivery Date', getValue: (r) => r.delivery_date || '-' },
    { header: 'Delivery Time', getValue: (r) => r.delivery_time || '-' },
    { header: 'Delivery Day', getValue: (r) => r.delivery_day || '-' },
    { header: 'Status', getValue: (r) => r.status || '-' },
    { header: 'Branch', getValue: (r) => r.branch || '-' },
    { header: 'Bill ID', getValue: (r) => r.bill_id || '-' }
];

const itemReportColumns = [
    { header: 'Order No', getValue: (r) => r.ord_no || '-' },
    { header: 'Order Date', getValue: (r) => r.ord_date || '-' },
    { header: 'Invoice No', getValue: (r) => r.invoice_no || '-' },
    { header: 'Invoice Date', getValue: (r) => r.invoice_date || '-' },
    { header: 'Invoice Time', getValue: (r) => r.invoice_time || '-' },
    { header: 'Pay Mode', getValue: (r) => r.pay_mode || '-' },
    { header: 'Category', getValue: (r) => r.cat_name || '-' },
    { header: 'Product ID', getValue: (r) => r.product_id || '-' },
    { header: 'Product Name', getValue: (r) => r.product_name || '-' },
    { header: 'UOM', getValue: (r) => r.uom || '-' },
    { header: 'Basic Rate', getValue: (r) => num(r.basic_rate), numeric: true },
    { header: 'Qty', getValue: (r) => num(r.quantity), numeric: true },
    { header: 'Pc Qty', getValue: (r) => num(r.pc_qty), numeric: true },
    { header: 'Basic Price', getValue: (r) => num(r.basic_price), numeric: true },
    { header: 'Discount %', getValue: (r) => num(r.disc_percentage), numeric: true, skipSum: true },
    { header: 'Discount', getValue: (r) => num(r.disc_amount), numeric: true },
    { header: 'Taxable', getValue: (r) => num(r.taxable_amt), numeric: true },
    { header: 'Tax %', getValue: (r) => num(r.tax_percentage), numeric: true, skipSum: true },
    { header: 'Tax', getValue: (r) => num(r.tax_amt), numeric: true },
    { header: 'SGST %', getValue: (r) => num(r.sgst_percentage), numeric: true, skipSum: true },
    { header: 'SGST Amt', getValue: (r) => num(r.sgst_amt), numeric: true },
    { header: 'CGST %', getValue: (r) => num(r.cgst_percentage), numeric: true, skipSum: true },
    { header: 'CGST Amt', getValue: (r) => num(r.cgst_amt), numeric: true },
    { header: 'Total', getValue: (r) => num(r.total_price), numeric: true },
    { header: 'Bill Status', getValue: (r) => r.bill_status || '-' },
    { header: 'Bill ID', getValue: (r) => r.bill_id || '-' },
    { header: 'Customer Name', getValue: (r) => r.customer_name || '-' }
];

const ordersItemReportColumns = [
    { header: 'Product Name', getValue: (r) => r.product_name || '-' },
    { header: 'Category', getValue: (r) => r.category_name || '-' },
    { header: 'Qty', getValue: (r) => num(r.total_qty), numeric: true },
    { header: 'Discount', getValue: (r) => num(r.discount_amount), numeric: true },
    { header: 'Taxable', getValue: (r) => num(r.taxable_amount), numeric: true },
    { header: 'Tax', getValue: (r) => num(r.tax_amount), numeric: true },
    { header: 'Total', getValue: (r) => num(r.total_amount), numeric: true }
];

const categoryReportColumns = [
    { header: 'Category', getValue: (r) => r.category_name || '-' },
    { header: 'Qty', getValue: (r) => num(r.total_qty), numeric: true },
    { header: 'Taxable', getValue: (r) => num(r.taxable_amount), numeric: true },
    { header: 'Tax', getValue: (r) => num(r.tax_amount), numeric: true },
    { header: 'Total', getValue: (r) => num(r.total_amount), numeric: true }
];

const ordersProductsColumns = [
    { header: 'Product ID', getValue: (r) => r.spl_id || '-' },
    { header: 'Product Name', getValue: (r) => r.product_name || '-' },
    { header: 'Product No', getValue: (r) => r.product_no || '-' },
    { header: 'HSN Code', getValue: (r) => r.hsn_code || '-' },
    { header: 'UOM', getValue: (r) => r.uom || '-' },
    { header: 'Basic Rate', getValue: (r) => num(r.basic_rate), numeric: true },
    { header: 'Tax %', getValue: (r) => num(r.basic_tax_percent), numeric: true, skipSum: true },
    { header: 'Price', getValue: (r) => num(r.price), numeric: true },
    { header: 'Status', getValue: (r) => r.product_status || '-' },
    { header: 'Category', getValue: (r) => r.category_name || '-' }
];

const ordersCustomersProductsColumns = [
    { header: 'Customer', getValue: (r) => r.company_name || '-' },
    { header: 'Phone', getValue: (r) => r.phone_number || '-' },
    { header: 'GST No', getValue: (r) => r.gst_no || '-' },
    { header: 'Product Name', getValue: (r) => r.product_name || '-' },
    { header: 'HSN Code', getValue: (r) => r.hsn_code || '-' },
    { header: 'UOM', getValue: (r) => r.uom || '-' },
    { header: 'Basic Rate', getValue: (r) => num(r.basic_rate), numeric: true },
    { header: 'Price', getValue: (r) => num(r.price), numeric: true },
    { header: 'Category', getValue: (r) => r.category_name || '-' },
    { header: 'Status', getValue: (r) => r.product_status || '-' }
];

const ordersCustomerReportColumns = [
    { header: 'Bill ID', getValue: (r) => r.bill_id || '-' },
    { header: 'Invoice No', getValue: (r) => r.invoice_no || '-' },
    { header: 'Date', getValue: (r) => formatDate(r.invoice_date) },
    { header: 'Time', getValue: (r) => r.invoice_time || '-' },
    { header: 'Order Type', getValue: (r) => r.order_type || '-' },
    { header: 'Customer', getValue: (r) => r.company_name || '-' },
    { header: 'Phone', getValue: (r) => r.phone_number || '-' },
    { header: 'GST No', getValue: (r) => r.gst_no || '-' },
    { header: 'Basic', getValue: (r) => num(r.total_basic_price), numeric: true },
    { header: 'Discount', getValue: (r) => num(r.disc_total_amt), numeric: true },
    { header: 'Tax', getValue: (r) => num(r.tax_aft_disc), numeric: true },
    { header: 'Total', getValue: (r) => num(r.total_paid_amount), numeric: true },
    { header: 'Balance', getValue: (r) => num(r.balance_amount), numeric: true },
    { header: 'MOP', getValue: (r) => r.final_mop || '-' },
    { header: 'Status', getValue: (r) => r.status || '-' },
    { header: 'Settlement Status', getValue: (r) => r.stlmnt_status || '-' }
];

const COUNTER_MASTERS = [
    { key: 'counter_products', label: 'Products', description: 'Raw product data (server database), with category name.', needsDate: false, fetchFn: exportCounterProducts, columns: counterProductsColumns, sheetName: 'Counter Products' },
    { key: 'counter_categories', label: 'Category', description: 'Raw product category data (server database).', needsDate: false, fetchFn: exportCounterCategories, columns: categoryColumns, sheetName: 'Counter Categories' }
];

const COUNTER_REPORTS = [
    { key: 'counter_bill_report', label: 'Bill Report', description: 'Bill-level detail (one row per bill) for the selected date range.', needsDate: true, fetchFn: exportCounterBillReport, columns: billReportColumns, sheetName: 'Counter Bill Report' },
    { key: 'counter_item_report', label: 'Item Report', description: 'Bill-wise, line-item sales detail for the selected date range.', needsDate: true, fetchFn: exportCounterItemReport, columns: itemReportColumns, sheetName: 'Counter Item Report' },
    { key: 'counter_category_report', label: 'Category Report', description: 'Sales grouped by category name for the selected date range.', needsDate: true, fetchFn: exportCounterCategoryReport, columns: categoryReportColumns, sheetName: 'Counter Category Report' }
];

const ORDERS_MASTERS = [
    { key: 'orders_products', label: 'Products', description: 'Raw order product data (client database), with category name.', needsDate: false, fetchFn: exportOrdersProducts, columns: ordersProductsColumns, sheetName: 'Orders Products' },
    { key: 'orders_categories', label: 'Category', description: 'Raw product category data (client database).', needsDate: false, fetchFn: exportOrdersCategories, columns: categoryColumns, sheetName: 'Orders Categories' },
    { key: 'orders_customers_products', label: 'Customers with Products', description: 'Order products joined with their assigned customer.', needsDate: false, fetchFn: exportOrdersCustomersProducts, columns: ordersCustomersProductsColumns, sheetName: 'Customers with Products' }
];

const ORDERS_REPORTS = [
    { key: 'orders_item_report', label: 'Item Report', description: 'Sales grouped by product name for the selected date range (Institution + ODC).', needsDate: true, fetchFn: exportOrdersItemReport, columns: ordersItemReportColumns, sheetName: 'Orders Item Report' },
    { key: 'orders_category_report', label: 'Category Report', description: 'Sales grouped by category name for the selected date range (Institution + ODC).', needsDate: true, fetchFn: exportOrdersCategoryReport, columns: categoryReportColumns, sheetName: 'Orders Category Report' },
    { key: 'orders_customer_report', label: 'Customer wise Report', description: 'Bill list with customer details for the selected date range (same shape as Bill Report).', needsDate: true, fetchFn: exportOrdersCustomerReport, columns: ordersCustomerReportColumns, sheetName: 'Orders Customer Report' }
];

const SECTIONS = [
    {
        key: 'counter',
        label: 'Counter Section',
        icon: 'pi-shopping-cart',
        description: 'Dine-in / counter sales data.',
        masters: COUNTER_MASTERS,
        reports: COUNTER_REPORTS
    },
    {
        key: 'orders',
        label: 'Orders Section',
        icon: 'pi-truck',
        description: 'Institution Sales + ODC (Outdoor Catering) combined.',
        masters: ORDERS_MASTERS,
        reports: ORDERS_REPORTS
    }
];

const ExportRow = ({ def, icon, disabled, loadingKey, onDownload }) => (
    <div className="excel-export-row">
        <div className="excel-export-row-left">
            <div className="excel-export-row-icon">
                <i className={`pi ${icon}`} />
            </div>
            <div>
                <div className="excel-export-row-title">{def.label}</div>
                <div className="excel-export-row-desc">{def.description}</div>
            </div>
        </div>
        <Button label="Download" icon="pi pi-download" className="p-button-sm p-button-outlined" disabled={disabled} loading={loadingKey === def.key} onClick={() => onDownload(def)} />
    </div>
);

const ExcelExport = () => {
    const toast = useRef(null);

    const role = (localStorage.getItem('role') || '').toUpperCase();
    const isGspos = role === 'GSPOS';
    const isManager = role === 'MANAGER';
    const ownClientId = localStorage.getItem('clientid') || '';
    const ownBranchId = localStorage.getItem('branchid') || '';
    const ownBranchName = localStorage.getItem('branch') || ownBranchId;
    const managerBranchInit = useRef(false);

    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [downloadingKey, setDownloadingKey] = useState(null);
    const [activeSection, setActiveSection] = useState(SECTIONS[0].key);

    const effectiveClientId = isGspos ? selectedClient?.client_id || '' : ownClientId;
    const effectiveClientLabel = isGspos ? selectedClient?.client_name || '' : '';

    useEffect(() => {
        if (!isGspos) return;
        getClientsList()
            .then(setClients)
            .catch(() => setClients([]));
    }, [isGspos]);

    useEffect(() => {
        if (!effectiveClientId) {
            setBranches([]);
            return;
        }
        getBillFilterHeader({ rolename: role || 'CEO', clientname: effectiveClientId })
            .then((res) => {
                const raw = Array.isArray(res?.branchs) ? res.branchs : [];
                const hasAll = raw.some((b) => String(b?.branch_id).toLowerCase() === 'all');
                setBranches(hasAll ? raw : [{ branch_id: 'All', branch_name: 'All Branches' }, ...raw]);
            })
            .catch(() => setBranches([]));
    }, [effectiveClientId, role]);

    // MANAGER: branch is hidden, always the manager's own branch
    useEffect(() => {
        if (!isManager || managerBranchInit.current || !ownBranchId) return;
        setSelectedBranch({ branch_id: ownBranchId, branch_name: ownBranchName });
        managerBranchInit.current = true;
    }, [isManager, ownBranchId, ownBranchName]);

    // Reset branch selection whenever the client changes (GSPOS switching clients)
    useEffect(() => {
        if (isManager) return;
        setSelectedBranch(null);
    }, [effectiveClientId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDownload = async (def) => {
        if (!effectiveClientId) {
            toast.current?.show({ severity: 'warn', summary: 'Client required', detail: 'Please select a client first.', life: 4000 });
            return;
        }
        if (def.needsDate && (!fromDate || !toDate)) {
            toast.current?.show({ severity: 'warn', summary: 'Date range required', detail: 'Please select from and to date for this report.', life: 4000 });
            return;
        }

        setDownloadingKey(def.key);
        try {
            const payload = { client_id: effectiveClientId, branch_id: selectedBranch?.branch_id || '' };
            if (def.needsDate) {
                payload.from_date = formatDateForApi(fromDate);
                payload.to_date = formatDateForApi(toDate);
            }
            const rows = await def.fetchFn(payload);
            if (!rows.length) {
                toast.current?.show({ severity: 'info', summary: 'No data', detail: 'No records found for the selected filters.', life: 4000 });
                return;
            }

            const filterLines = [];
            if (def.needsDate) filterLines.push(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`);
            if (effectiveClientLabel) filterLines.push(`Client: ${effectiveClientLabel}`);
            filterLines.push(`Branch: ${selectedBranch?.branch_name || 'All Branches'}`);
            filterLines.push(`Generated On: ${new Date().toLocaleString('en-IN')}`);

            await exportStyledExcel({
                fileName: `${def.sheetName.replace(/\s+/g, '')}_${moment().format('YYYY-MM-DD')}.xlsx`,
                sheetName: def.sheetName,
                title: def.label,
                filterLines,
                columns: def.columns,
                rows
            });
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Export failed', detail: err?.response?.data?.message || err?.message || 'Failed to export data', life: 5000 });
        } finally {
            setDownloadingKey(null);
        }
    };

    const rowsDisabled = !effectiveClientId;
    const activeSectionDef = SECTIONS.find((s) => s.key === activeSection) || SECTIONS[0];

    return (
        <div className="excel-export-container">
            <Toast ref={toast} />

            <div className="excel-export-hero">
                <h2>
                    <i className="pi pi-file-export" />
                    Excel Export
                </h2>
                <p>Download raw master data and reports as Excel, based on client and branch.</p>
            </div>

            <Card className="excel-export-filters">
                <div className="excel-export-filters-title">
                    <i className="pi pi-filter" />
                    Filters
                </div>
                <div className="flex flex-wrap align-items-end gap-3">
                    {isGspos && (
                        <div className="flex flex-column" style={{ flex: '1 1 200px', minWidth: '180px' }}>
                            <label htmlFor="client">Client</label>
                            <Dropdown id="client" value={selectedClient} options={clients} onChange={(e) => setSelectedClient(e.value)} optionLabel="client_name" placeholder="Select Client" className="w-full" filter showClear />
                        </div>
                    )}

                    {!isManager && (
                        <div className="flex flex-column" style={{ flex: '1 1 200px', minWidth: '180px' }}>
                            <label htmlFor="branch">Branch</label>
                            <Dropdown
                                id="branch"
                                value={selectedBranch}
                                options={branches}
                                onChange={(e) => setSelectedBranch(e.value)}
                                optionLabel="branch_name"
                                placeholder="All Branches"
                                className="w-full"
                                filter
                                showClear
                                disabled={!effectiveClientId}
                            />
                        </div>
                    )}

                    <div className="flex flex-column" style={{ flex: '1 1 160px', minWidth: '150px' }}>
                        <label htmlFor="fromDate">
                            From Date <span className="text-color-secondary text-sm">(for Reports)</span>
                        </label>
                        <Calendar id="fromDate" value={fromDate} onChange={(e) => setFromDate(e.value)} dateFormat="dd-mm-yy" showIcon placeholder="From Date" className="w-full" showButtonBar />
                    </div>

                    <div className="flex flex-column" style={{ flex: '1 1 160px', minWidth: '150px' }}>
                        <label htmlFor="toDate">
                            To Date <span className="text-color-secondary text-sm">(for Reports)</span>
                        </label>
                        <Calendar id="toDate" value={toDate} onChange={(e) => setToDate(e.value)} dateFormat="dd-mm-yy" showIcon placeholder="To Date" minDate={fromDate} className="w-full" showButtonBar />
                    </div>
                </div>
            </Card>

            <div className="excel-export-layout">
                <div className="excel-export-nav">
                    {SECTIONS.map((s) => (
                        <button
                            key={s.key}
                            type="button"
                            className={`excel-export-nav-item ${activeSection === s.key ? 'active' : ''}`}
                            onClick={() => setActiveSection(s.key)}
                        >
                            <span className="excel-export-nav-icon">
                                <i className={`pi ${s.icon}`} />
                            </span>
                            <span className="excel-export-nav-text">
                                <span className="excel-export-nav-label">{s.label}</span>
                                <span className="excel-export-nav-sub">{s.masters.length + s.reports.length} downloads available</span>
                            </span>
                            <i className="pi pi-chevron-right excel-export-nav-chevron" />
                        </button>
                    ))}
                </div>

                <div className="excel-export-content">
                    <Card className="excel-export-panel">
                        <div className="excel-export-panel-header">
                            <div className="excel-export-panel-icon">
                                <i className={`pi ${activeSectionDef.icon}`} />
                            </div>
                            <div>
                                <h3>{activeSectionDef.label}</h3>
                                <p>{activeSectionDef.description}</p>
                            </div>
                        </div>

                        <Divider />
                        <div className="excel-export-group-title">
                            <i className="pi pi-database" />
                            Masters
                        </div>
                        <div className="excel-export-list">
                            {activeSectionDef.masters.map((def) => (
                                <ExportRow key={def.key} def={def} icon="pi-table" disabled={rowsDisabled} loadingKey={downloadingKey} onDownload={handleDownload} />
                            ))}
                        </div>

                        <Divider />
                        <div className="excel-export-group-title">
                            <i className="pi pi-chart-bar" />
                            Reports
                        </div>
                        <div className="excel-export-list">
                            {activeSectionDef.reports.map((def) => (
                                <ExportRow key={def.key} def={def} icon="pi-chart-line" disabled={rowsDisabled} loadingKey={downloadingKey} onDownload={handleDownload} />
                            ))}
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default ExcelExport;
